import { useEffect, useRef } from "react";

// Exactly 3 minutes of inactivity → auto-logout.
const IDLE_MS = 3 * 60 * 1000;

/**
 * Logs the user out after IDLE_MS of no interaction.
 * Pass the app's logout callback (from useApp().logout). Resets on any
 * user activity; also fires when returning to a tab that was idle past the limit.
 */
export function useIdleLogout(onIdle: () => void) {
  const lastActivity = useRef<number>(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleRef = useRef(onIdle);
  idleRef.current = onIdle;
  const firedRef = useRef(false);

  useEffect(() => {
    const fire = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      idleRef.current();
    };

    const reset = () => {
      lastActivity.current = Date.now();
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fire, IDLE_MS);
    };

    let throttled = false;
    const onActivity = () => {
      if (throttled) return;
      throttled = true;
      window.setTimeout(() => {
        throttled = false;
      }, 1000);
      reset();
    };

    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastActivity.current >= IDLE_MS
      ) {
        fire();
      }
    };

    const events = ["mousemove", "mousedown", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    reset();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);
}
